import { createContext, useContext, type ReactNode } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";

import { formatCurrency } from "helpers/currency";
import {
  EXTENDED_CARE_DAILY_FEE,
  WEIGHT_THRESHOLD_LBS,
} from "constants/at-need";

import type {
  ServiceQuestion as ServiceQuestionType,
  ServiceAnswer,
  PackageInclusions,
  DcOrderingTemplate,
} from "./types";
import { QUESTION_ID, getIncludedOfferingQuantity } from "./helpers";
import DeathCertificatesQuestion from "./DeathCertificatesQuestion";
import DeathCertStepper from "./DeathCertStepper";
import { LOVED_ONE_FALLBACK } from "./useLovedOneFirstName";

/**
 * Heading level for the question prompt. On the wizard path the prompt IS the
 * page heading, so it defaults to h1. The read-only view maps every question
 * onto one page under its own h1, so there each prompt drops to h2 — otherwise
 * that page carries six competing page headings. Carried by context so the
 * level doesn't have to thread through all six question variants.
 */
const QuestionHeadingLevelContext = createContext<1 | 2>(1);

/**
 * Centered to match the step headings on Package & Selections; the answer
 * options below stay left-aligned.
 */
function QuestionTitle({ children }: { children: ReactNode }) {
  const Tag = useContext(QuestionHeadingLevelContext) === 1 ? "h1" : "h2";
  return (
    <Tag className="mb-3 text-center font-serif text-3xl text-evergreen">
      {children}
    </Tag>
  );
}

type ServiceQuestionProps = {
  question: ServiceQuestionType;
  answer: ServiceAnswer | undefined;
  onAnswer: (answer: ServiceAnswer) => void;
  includedRefrigerationDays?: number;
  packageInclusions?: PackageInclusions | null;
  /** The `q-dc-ordering` answer. Only read by the death-certs question. */
  dcOrderingAnswer?: ServiceAnswer;
  /** Swaps the death-certs stepper for the self-serve ordering choice. */
  dcSelfServeEnabled?: boolean;
  /** County/state ordering guidance for the self-serve option. */
  dcOrderingTemplate?: DcOrderingTemplate | null;
  /** Per-county and per-market timings for the two options. */
  dcTimelineFamilyOrders?: string | null;
  dcTimelineAfterOrders?: string | null;
  /** The loved one's first name, for the death-certs heading. */
  dcLovedOneName?: string;
  /**
   * The case's own ordering preference, when it outranks the draft answer.
   */
  dcSelectedOrdering?: string | null;
  /**
   * Renders the death-certs question as a statement: the certificates have
   * been ordered, so neither who orders them nor how many is still open. Also
   * applies to the plain stepper, which is what a family sees for the same
   * question while the self-serve choice is switched off.
   */
  dcReadOnly?: boolean;
  /**
   * How many certificates the case records as ordered, once an order exists.
   * Only read by the death-certs question, on both its screens.
   */
  dcOrderedQuantity?: number;
};

export default function ServiceQuestion({
  headingLevel = 1,
  ...props
}: ServiceQuestionProps & { headingLevel?: 1 | 2 }) {
  return (
    <QuestionHeadingLevelContext.Provider value={headingLevel}>
      <QuestionBody {...props} />
    </QuestionHeadingLevelContext.Provider>
  );
}

function QuestionBody({
  question,
  answer,
  onAnswer,
  includedRefrigerationDays,
  packageInclusions,
  dcOrderingAnswer,
  dcSelfServeEnabled,
  dcOrderingTemplate,
  dcTimelineFamilyOrders,
  dcTimelineAfterOrders,
  dcLovedOneName,
  dcSelectedOrdering,
  dcReadOnly,
  dcOrderedQuantity,
}: ServiceQuestionProps) {
  const headingLevel = useContext(QuestionHeadingLevelContext);

  // Owns both the who-orders choice and the quantity, so it replaces the
  // generic stepper rather than wrapping it.
  if (dcSelfServeEnabled && question.id === QUESTION_ID.DEATH_CERTS) {
    return (
      <DeathCertificatesQuestion
        question={question}
        answer={answer}
        orderingAnswer={dcOrderingAnswer}
        onAnswer={onAnswer}
        packageInclusions={packageInclusions}
        template={dcOrderingTemplate}
        timelineFamilyOrders={dcTimelineFamilyOrders}
        timelineAfterOrders={dcTimelineAfterOrders}
        lovedOneName={dcLovedOneName ?? LOVED_ONE_FALLBACK}
        headingLevel={headingLevel}
        selectedOrdering={dcSelectedOrdering}
        readOnly={dcReadOnly}
        orderedQuantity={dcOrderedQuantity}
      />
    );
  }

  if (question.id === QUESTION_ID.REFRIGERATION) {
    return (
      <RefrigerationInfo
        question={question}
        includedDays={includedRefrigerationDays ?? 3}
      />
    );
  }

  if (question.id === QUESTION_ID.WEIGHT) {
    return <WeightInfo question={question} />;
  }

  if (question.type === "info") {
    return <InfoQuestion question={question} />;
  }

  if (question.type === "stepper") {
    return (
      <StepperQuestion
        question={question}
        answer={answer}
        onAnswer={onAnswer}
        dcReadOnly={dcReadOnly && question.id === QUESTION_ID.DEATH_CERTS}
        dcOrderedQuantity={
          question.id === QUESTION_ID.DEATH_CERTS
            ? dcOrderedQuantity
            : undefined
        }
      />
    );
  }

  if (question.id === QUESTION_ID.RUSH_CREMATION) {
    return (
      <RushCremationQuestion
        question={question}
        answer={answer}
        onAnswer={onAnswer}
        packageInclusions={packageInclusions}
      />
    );
  }

  return (
    <RadioQuestion
      question={question}
      answer={answer}
      onAnswer={onAnswer}
      packageInclusions={packageInclusions}
    />
  );
}

function RadioQuestion({
  question,
  answer,
  onAnswer,
  packageInclusions,
}: ServiceQuestionProps) {
  return (
    <div>
      <QuestionTitle>{question.question}</QuestionTitle>
      {question.note && (
        <p className="mb-4 text-sm text-soil">{question.note}</p>
      )}
      <RadioOptionList
        question={question}
        answer={answer}
        onAnswer={onAnswer}
        packageInclusions={packageInclusions}
      />
    </div>
  );
}

function RadioOptionList({
  question,
  answer,
  onAnswer,
  packageInclusions,
}: ServiceQuestionProps) {
  return (
    <div className="space-y-3">
      {question.options?.map((option) => {
        const isSelected = answer?.selectedOptionId === option.id;
        const isIncluded =
          !!option.offeringId &&
          getIncludedOfferingQuantity(
            packageInclusions ?? null,
            option.offeringId,
          ) > 0;
        const price = isIncluded ? 0 : option.price;
        const priceLabel = isIncluded ? "Included" : formatCurrency(price);

        return (
          <button
            key={option.id}
            onClick={() =>
              onAnswer({
                questionId: question.id,
                selectedOptionId: option.id,
              })
            }
            className={`flex w-full items-center rounded-md border p-4 text-left transition ${
              isSelected
                ? "border-evergreen bg-ivory font-bold"
                : "border-olive bg-white hover:bg-ivory"
            }`}
          >
            <div className="mr-4">
              <div
                className={`h-3 w-3 rounded-full outline outline-2 outline-offset-2 outline-evergreen ${
                  isSelected ? "bg-cantelope" : ""
                }`}
              />
            </div>
            <div className="flex-1">
              <span className="text-evergreen">{option.label}</span>
              {isIncluded && price === 0 && (
                <p className="mt-1 text-xs font-medium text-evergreen">
                  Included in your plan
                </p>
              )}
              {option.note && (
                <p className="mt-1 text-xs text-soil">{option.note}</p>
              )}
            </div>
            {(price > 0 || isIncluded) && (
              <span
                className={`ml-4 ${isIncluded ? "font-medium text-sage" : "text-evergreen"}`}
              >
                {priceLabel}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TimelineRow({
  label,
  value,
  cremationClass,
  caption,
}: {
  label: string;
  value: string;
  cremationClass: string;
  caption: string;
}) {
  // Bar length encodes total elapsed days: both permits segments are the
  // same fixed share of an identically-sized container (label and value
  // columns are fixed-width so the bars align), and the bar ends right
  // after its colored cremation segment — no track is drawn past it.
  return (
    <div>
      <div className="flex items-center gap-4">
        <span className="w-20 shrink-0 text-base font-medium text-evergreen">
          {label}
        </span>
        <div className="flex-1">
          <div className="flex h-2.5">
            <div className="w-[58%] rounded-l-full bg-oat" />
            <div className={`rounded-r-full ${cremationClass}`} />
          </div>
        </div>
        <span className="w-28 shrink-0 whitespace-nowrap text-right text-base font-bold text-evergreen">
          {value}
        </span>
      </div>
      <p className="mt-1.5 text-sm text-soil">{caption}</p>
    </div>
  );
}

function RushCremationQuestion({
  question,
  answer,
  onAnswer,
  packageInclusions,
}: ServiceQuestionProps) {
  return (
    <div>
      <QuestionTitle>{question.question}</QuestionTitle>
      {question.note && (
        <p className="text-base leading-relaxed text-soil">{question.note}</p>
      )}

      <div className="mt-6 rounded-md border border-oat bg-white p-4 sm:p-5">
        <div className="space-y-5">
          <TimelineRow
            label="Standard"
            value="~10–12 days"
            cremationClass="w-[42%] bg-soil/50"
            caption="6–7 days county permits, then 4–5 days cremation"
          />
          <TimelineRow
            label="Rush"
            value="~7–9 days"
            cremationClass="w-[9%] bg-cantelope"
            caption="Same 6–7 days permits, then 24–48 hours cremation"
          />
        </div>
      </div>

      <div className="mt-8">
        <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-soil/70">
          How it works
        </p>
        <p className="text-base leading-relaxed text-soil">
          The moment the county issues permits, we move your loved one to the
          front of the schedule, so the wait doesn't stretch longer than it has
          to.
        </p>
      </div>

      <div className="mt-8">
        <p className="mb-2 text-sm font-semibold uppercase tracking-wide text-soil/70">
          Why families choose this
        </p>
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-base text-soil">
            <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-sage" />
            <span>Plan the service or gathering sooner, not weeks later</span>
          </li>
          <li className="flex items-start gap-2 text-base text-soil">
            <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-sage" />
            <span>Simpler travel plans for family coming from out of town</span>
          </li>
          <li className="flex items-start gap-2 text-base text-soil">
            <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-sage" />
            <span>Fewer days waiting on something you can't control</span>
          </li>
        </ul>
      </div>

      <div className="mt-8">
        <RadioOptionList
          question={question}
          answer={answer}
          onAnswer={onAnswer}
          packageInclusions={packageInclusions}
        />
      </div>

      <div className="mt-6 border-t border-oat pt-4">
        <p className="text-lg font-bold text-evergreen">
          What if the medical examiner needs to review first?
        </p>
        <p className="mt-2 text-base leading-relaxed text-soil">
          Cases referred for medical examiner review or involving an
          out-of-state death can take longer than 10 days for permits. We'll
          keep you updated, and rush service still applies once permits clear.
        </p>
      </div>
    </div>
  );
}

function InfoQuestion({ question }: { question: ServiceQuestionType }) {
  return (
    <div>
      <QuestionTitle>{question.question}</QuestionTitle>
      {question.note && (
        <div className="rounded-md border border-oat bg-ivory p-5">
          <p className="text-sm leading-relaxed text-soil">{question.note}</p>
        </div>
      )}
    </div>
  );
}

function WeightInfo({ question }: { question: ServiceQuestionType }) {
  const ctx = question.weightContext;

  const threshold = ctx?.threshold ?? WEIGHT_THRESHOLD_LBS;
  const weight = ctx?.weight;
  const facilityName = ctx?.facilityName;
  const feeAmount = ctx?.feeAmount;
  const hasWeight = weight !== null && weight !== undefined;
  const hasFeeData = hasWeight && feeAmount !== null; // backend successfully calculated
  const hasFee = hasFeeData && feeAmount != null && feeAmount > 0;

  return (
    <div>
      <QuestionTitle>A note about weight-related fees</QuestionTitle>

      <div className="space-y-4 text-lg leading-relaxed text-soil">
        <p>
          Additional fees may apply if your loved one weighs{" "}
          <span className="font-bold text-evergreen">
            {threshold} lbs or more
          </span>
          , to ensure their honorable transportation and care.
        </p>

        <div className="rounded-md border border-sage/30 bg-sage/10 p-4">
          {hasWeight && hasFeeData ? (
            hasFee ? (
              <p className="text-lg text-evergreen">
                {facilityName ? `${facilityName} has` : "We have"} reported a
                weight of <span className="font-bold">{weight} lbs</span>. Based
                on this, an additional fee of{" "}
                <span className="font-bold">{formatCurrency(feeAmount!)}</span>{" "}
                applies. This has been included in your arrangement total. If
                this information changes, we will notify you of the necessary
                adjustments.
              </p>
            ) : (
              <p className="text-lg text-evergreen">
                {facilityName ? `${facilityName} has` : "We have"} reported a
                weight of <span className="font-bold">{weight} lbs</span>. Based
                on this, no additional weight-related fees apply to your
                arrangement. If this information changes, we will notify you of
                any additional charges.
              </p>
            )
          ) : (
            <p className="text-lg text-evergreen">
              Weight information reported by the care facility will be used to
              determine if additional fees apply. Your care specialist will
              notify you of any applicable charges.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function RefrigerationInfo({
  question,
  includedDays,
}: {
  question: ServiceQuestionType;
  includedDays: number;
}) {
  return (
    <div>
      <QuestionTitle>{question.question}</QuestionTitle>

      <div className="space-y-4 text-lg leading-relaxed text-soil">
        <p>
          Your plan includes{" "}
          <span className="font-bold text-evergreen">
            {includedDays} business days
          </span>{" "}
          of secure refrigeration at no additional cost. This window begins once
          your loved one is in our care.
        </p>

        <p>
          During this time, our team will be working to obtain the required
          cremation permits and prepare everything on our end. To help us stay
          within this window and avoid any additional fees, we ask that your
          family complete two things as soon as possible:
        </p>

        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <span className="font-medium text-evergreen">
              Finalize your arrangement selections
            </span>{" "}
            — confirm your package details and any add-ons
          </li>
          <li>
            <span className="font-medium text-evergreen">
              Submit vital records information
            </span>{" "}
            — the details we need to file required government documents
          </li>
        </ol>

        <p>
          If additional time is needed beyond the included {includedDays} days,
          a fee of{" "}
          <span className="font-medium text-evergreen">
            {formatCurrency(EXTENDED_CARE_DAILY_FEE)} per business day
          </span>{" "}
          applies. We want to be upfront about this so there are no surprises
          for your family.
        </p>
      </div>
    </div>
  );
}

function StepperQuestion({
  question,
  answer,
  onAnswer,
  dcReadOnly,
  dcOrderedQuantity,
}: ServiceQuestionProps) {
  return (
    <div>
      <QuestionTitle>{question.question}</QuestionTitle>
      {question.note && (
        <p className="mb-4 text-lg text-soil">{question.note}</p>
      )}
      <DeathCertStepper
        question={question}
        answer={answer}
        onAnswer={onAnswer}
        readOnly={dcReadOnly}
        orderedQuantity={dcOrderedQuantity}
      />
    </div>
  );
}
